import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  CheckCircle2,
  Clock,
  Target,
  Layers,
} from 'lucide-react';
import { usePipelineForecast, type ForecastProject } from '@/hooks/useForecast';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';

type ForecastTab = 'pipeline' | 'delivery';

const STAGE_LABELS: Record<string, string> = {
  partner_briefing: 'Partner Briefing',
  services_positioned: 'Services Positioned',
  scoping_workshop: 'Scoping Workshop',
  customer_review: 'Customer Review',
  customer_approval: 'Customer Approval',
  po_sent: 'PO Sent',
  in_delivery: 'In Delivery',
};

export function Forecast() {
  const [activeTab, setActiveTab] = useState<ForecastTab>('pipeline');

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="font-heading text-xl md:text-2xl font-bold text-xtravirt-dark">
          Forecast
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Pipeline and delivery revenue forecasting
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4 md:gap-6">
          <TabButton
            active={activeTab === 'pipeline'}
            onClick={() => setActiveTab('pipeline')}
            icon={Target}
            label="Pipeline Forecast"
          />
          <TabButton
            active={activeTab === 'delivery'}
            onClick={() => setActiveTab('delivery')}
            icon={Layers}
            label="Delivery Forecast"
          />
        </nav>
      </div>

      {activeTab === 'pipeline' && <PipelineForecastTab />}
      {activeTab === 'delivery' && <DeliveryForecastTab />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 py-3 border-b-2 text-sm font-medium transition-colors',
        active
          ? 'border-xtravirt-cyan text-xtravirt-cyan'
          : 'border-transparent text-gray-600 hover:text-xtravirt-dark'
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

// ============ Pipeline Forecast Tab ============

function PipelineForecastTab() {
  const { data, isLoading } = usePipelineForecast();

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (!data) {
    return <div className="card p-6 text-center text-gray-500">Failed to load forecast</div>;
  }

  const { summary, funnel, committed, pipeline } = data;

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Info banner */}
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
        <strong>Phase 1 forecast:</strong> committed = projects past PO (po_sent + in_delivery).
        Pipeline is weighted automatically by stage. Per-project confidence overrides coming later.
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <SummaryCard
          icon={CheckCircle2}
          label="Committed"
          value={formatCurrency(summary.committedValue)}
          subLabel={`${summary.committedCount} project${summary.committedCount !== 1 ? 's' : ''}`}
          color="green"
        />
        <SummaryCard
          icon={Target}
          label="Pipeline (Weighted)"
          value={formatCurrency(summary.pipelineWeightedValue)}
          subLabel={`of ${formatCurrency(summary.pipelineRawValue)} raw`}
          color="cyan"
        />
        <SummaryCard
          icon={TrendingUp}
          label="Total Forecast"
          value={formatCurrency(summary.totalForecast)}
          subLabel="Committed + Weighted"
          color="dark"
        />
        <SummaryCard
          icon={Clock}
          label="Pipeline Count"
          value={summary.pipelineCount.toString()}
          subLabel="Projects in scoping"
          color="amber"
        />
      </div>

      {/* Funnel */}
      <div className="card p-4 md:p-6">
        <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark mb-4">
          Stage Funnel
        </h3>
        <div className="space-y-3">
          {funnel.map((stage) => {
            const pct = summary.pipelineRawValue + summary.committedValue > 0
              ? (stage.rawValue / (summary.pipelineRawValue + summary.committedValue)) * 100
              : 0;
            return (
              <div key={stage.stage} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {STAGE_LABELS[stage.stage] || stage.stage}
                    </span>
                    <span className="text-xs text-gray-400">
                      {Math.round(stage.weight * 100)}% weight
                    </span>
                    <span className="text-xs text-gray-500">
                      &middot; {stage.count} project{stage.count !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="font-semibold text-xtravirt-dark">
                      {formatCurrency(stage.weightedValue)}
                    </span>
                    {stage.weight < 1 && stage.rawValue > 0 && (
                      <span className="text-xs text-gray-400 ml-1">
                        / {formatCurrency(stage.rawValue)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={cn(
                      'absolute left-0 top-0 h-full rounded-full',
                      stage.weight >= 1 ? 'bg-green-500' : 'bg-xtravirt-cyan'
                    )}
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <div className="card p-4 md:p-6">
          <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark mb-3">
            Committed ({committed.length})
          </h3>
          {committed.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No committed projects</p>
          ) : (
            <div className="space-y-2">
              {committed.map((p) => (
                <ForecastProjectRow key={p.id} project={p} showWeight={false} />
              ))}
            </div>
          )}
        </div>

        <div className="card p-4 md:p-6">
          <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark mb-3">
            Pipeline ({pipeline.length})
          </h3>
          {pipeline.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No pipeline projects</p>
          ) : (
            <div className="space-y-2">
              {pipeline.map((p) => (
                <ForecastProjectRow key={p.id} project={p} showWeight />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Delivery Forecast Tab (placeholder) ============

function DeliveryForecastTab() {
  return (
    <div className="card p-12 text-center">
      <Layers className="h-12 w-12 mx-auto text-gray-300 mb-3" />
      <h3 className="font-medium text-gray-900 mb-1">Delivery Forecast — Coming Soon</h3>
      <p className="text-sm text-gray-500 max-w-md mx-auto">
        Monthly revenue recognition from in-delivery projects will be calculated from the
        weekly delivery allocations. This view will show committed revenue per month across
        all active engagements.
      </p>
    </div>
  );
}

// ============ Summary Card ============

function SummaryCard({
  icon: Icon,
  label,
  value,
  subLabel,
  color,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  subLabel?: string;
  color: 'green' | 'cyan' | 'amber' | 'dark';
}) {
  const colorMap = {
    green: 'text-green-600',
    cyan: 'text-xtravirt-cyan',
    amber: 'text-amber-600',
    dark: 'text-xtravirt-dark',
  };
  return (
    <div className="card p-4">
      <div className="flex items-center gap-3">
        <Icon className={cn('h-8 w-8 flex-shrink-0', colorMap[color])} />
        <div className="min-w-0">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">{label}</p>
          <p className="text-lg md:text-xl font-heading font-bold text-xtravirt-dark mt-0.5 truncate">
            {value}
          </p>
          {subLabel && <p className="text-xs text-gray-400 truncate">{subLabel}</p>}
        </div>
      </div>
    </div>
  );
}

// ============ Forecast Project Row ============

function ForecastProjectRow({ project, showWeight }: { project: ForecastProject; showWeight: boolean }) {
  return (
    <Link
      to={`/projects/${project.id}`}
      className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 hover:border-xtravirt-cyan hover:bg-gray-50 transition-colors"
    >
      <div className="min-w-0 flex-1">
        <p className="font-medium text-xtravirt-dark truncate">{project.name}</p>
        <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
          <span className="truncate max-w-[140px]">{project.customerName}</span>
          {project.currentScopingStage && (
            <span className="inline-block px-1.5 py-0.5 bg-gray-100 rounded">
              {STAGE_LABELS[project.currentScopingStage] || project.currentScopingStage}
            </span>
          )}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        {showWeight ? (
          <>
            <p className="font-semibold text-xtravirt-cyan text-sm">
              {formatCurrency(project.weightedValue, project.currency)}
            </p>
            <p className="text-xs text-gray-400">
              {Math.round(project.weight * 100)}% of {formatCurrency(project.totalCost, project.currency)}
            </p>
          </>
        ) : (
          <p className="font-semibold text-green-700 text-sm">
            {formatCurrency(project.totalCost, project.currency)}
          </p>
        )}
      </div>
    </Link>
  );
}

function formatCurrency(cents: number, currency: 'GBP' | 'USD' | 'EUR' = 'USD'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}
